import { useCallback, useState } from 'react';
import type { SharedJobPost } from './useSharedJobPosts';
import SuggestCandidatesModal from '../../provided_job_posts/components/SuggestCandidatesModal';
import ShareContentDialog from '../../provided_job_posts/components/ShareContentDialog';
import useBulkSuggestSharedJobPostCandidates from '../api/useBulkSuggestSharedJobPostCandidates';
import useSharedJobPostPdf from './useSharedJobPostPdf';

type Params = {
  sharedJobPosts: SharedJobPost[];
  selectedIds: Set<number>;
};

export default function useBulkSuggestSharedJobPosts({ sharedJobPosts, selectedIds }: Params) {
  const { downloadMultiplePdfs } = useSharedJobPostPdf();
  const [isWebShareDialogOpen, setIsWebShareDialogOpen] = useState(false);
  const [webShareText, setWebShareText] = useState('');
  const [isSuggestModalOpen, setIsSuggestModalOpen] = useState(false);
  const [suggestJobPosts, setSuggestJobPosts] = useState<
    Array<{
      id: number;
      companyName: string;
      position: string;
      location: string;
    }>
  >([]);
  const { mutateAsync: suggestCandidates } = useBulkSuggestSharedJobPostCandidates();

  // 選択中の求人の公開URLを共有用テキストにまとめてダイアログで表示する
  const handleWebShare = useCallback(() => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    // 公開URLを持つ求人だけを対象にする
    const availablePosts = sharedJobPosts.filter(
      (post) => ids.includes(post.id) && post.public_url
    );

    if (availablePosts.length === 0) {
      alert('共有可能なURLがありません');
      return;
    }

    // 共有用テキストを生成（1求人ごとのURL共有と同じ形式）
    const text = availablePosts
      .map((post) =>
        [post.company_name ?? '', post.title ?? '', post.public_url].filter((v) => v).join('\n')
      )
      .filter((v) => v)
      .join('\n\n');

    setWebShareText(text);
    setIsWebShareDialogOpen(true);
  }, [selectedIds, sharedJobPosts]);

  // 選択中の求人票PDFをZIPで一括ダウンロードする
  const handlePdfShare = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    try {
      const result = await downloadMultiplePdfs(ids);
      if (result.missingCount > 0) {
        alert(
          `${result.downloadedCount}件の求人票をダウンロードしました。${result.missingCount}件は取得できませんでした。`
        );
      }
    } catch (error) {
      console.error('PDFダウンロードエラー:', error);
      alert('PDFのダウンロードに失敗しました。もう一度お試しください。');
    }
  }, [selectedIds, downloadMultiplePdfs]);

  const handleSuggestToCandidates = useCallback(() => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    // 選択中のシェア求人をすべて対象にする
    const targets = sharedJobPosts.filter((post) => ids.includes(post.id));
    if (targets.length === 0) return;

    setSuggestJobPosts(
      targets.map((post) => ({
        id: post.id,
        companyName: post.company_name ?? '',
        position: post.job_position_name ?? '',
        location: post.working_location_label ?? '',
      }))
    );
    setIsSuggestModalOpen(true);
  }, [selectedIds, sharedJobPosts]);

  const dialogs = (
    <>
      <ShareContentDialog
        title="求人票のURLをコピーする"
        shareText={webShareText}
        open={isWebShareDialogOpen}
        onOpenChange={setIsWebShareDialogOpen}
      />
      {suggestJobPosts.length > 0 && (
        <SuggestCandidatesModal
          isOpen={isSuggestModalOpen}
          onClose={() => {
            setIsSuggestModalOpen(false);
            setSuggestJobPosts([]);
          }}
          jobPosts={suggestJobPosts}
          onSubmit={async (candidateIds) => {
            const sharedJobPostIds = suggestJobPosts.map((post) => post.id);
            await suggestCandidates({
              sharedJobPostIds,
              candidateProfileIds: candidateIds,
            });
          }}
          onSuccess={() => {
            setIsSuggestModalOpen(false);
            setSuggestJobPosts([]);
          }}
          isSharedJobPost={true}
        />
      )}
    </>
  );

  return {
    handleWebShare,
    handlePdfShare,
    handleSuggestToCandidates,
    dialogs,
  };
}
